import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onClick", "type", "isLoaded", "showTooltip", "size", "childMarginInlineStart", "childTruncateLength"];
import { useEffect, useMemo, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { AVATAR_SIZES, DEFAULT_PROPS } from '../Constants';
import { useParsedColorScheme } from './hooks/useParsedColorScheme';
import { getAvatarContainerProps, getAvatarContentProps, getAvatarContentWrapperProps } from '../lib/displayUtils';
import { delayUntilIdle } from '../lib/delayUntilIdle';
import { getDefaultAvatarType } from '../lib/getDefaultAvatarType';
import { useHubSpotUserAvatarSrc } from '../lib/useHubSpotUserAvatarSrc';
import { getBorderProps, getMarginProps } from '../lib/stylePropHelpers';
import { AvatarContainer } from './components/AvatarContainer';
import { AvatarContent } from './components/AvatarContent';
import { AvatarContentWrapper } from './components/AvatarContentWrapper';
import { Caption } from './components/Caption';
import { BaseContainer } from './defaultSvgIcons/BaseContainer';
import { getSocialBadgeSize, shouldShowSocialBadge, SOCIAL_BADGE_GAP } from './components/SocialBadge';
import styled from 'foundations-theming/utils';
import { useTrellisMigration } from 'trellis-migration-context/TrellisMigrationContext';
import { TrellisAvatarDisplay } from './TrellisAvatarDisplay';

// Reserve room to the right for the social badge, which is absolutely
// positioned and overflows the avatar. Only used when a badge actually renders,
// and sized to match the responsive badge so the reserved space tracks it.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SocialAvatarWrapper = styled.span.withConfig({
  displayName: "AvatarDisplay__SocialAvatarWrapper"
})(["display:inline-flex;padding-inline-end:", ";"], ({
  $reserve
}) => `${$reserve}px`);
const FoundationsAvatarDisplay = _ref => {
  let {
      children,
      onClick,
      type,
      isLoaded = true,
      showTooltip = DEFAULT_PROPS.showTooltip,
      size = DEFAULT_PROPS.size,
      childMarginInlineStart = DEFAULT_PROPS.childMarginInlineStart,
      childTruncateLength = DEFAULT_PROPS.childTruncateLength
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const propsWithDefaults = Object.assign({
    showTooltip,
    size,
    childMarginInlineStart,
    childTruncateLength
  }, rest);
  const [isTooltipLoaded, setIsTooltipLoaded] = useState(false);
  const {
    isHubspotUserAvatar,
    resolvedSrc,
    isHubspotUserAvatarLoaded
  } = useHubSpotUserAvatarSrc({
    src: rest.src,
    source: rest.source
  });
  useEffect(() => {
    const clear = delayUntilIdle(() => setIsTooltipLoaded(true));
    return () => clear();
  }, []);
  const iconType = useMemo(() => getDefaultAvatarType(type), [type]);
  const {
    parsedBackgroundColor,
    parsedIconColor
  } = useParsedColorScheme(iconType);
  const passedProps = Object.assign({}, propsWithDefaults, {
    src: resolvedSrc,
    backgroundColor: parsedBackgroundColor,
    iconColor: parsedIconColor,
    onClick,
    type,
    iconType
  });
  const avatarHoverProps = getAvatarContainerProps(passedProps, {
    isTooltipLoaded
  });
  const avatarContentWrapperProps = getAvatarContentWrapperProps(passedProps);
  const avatarContentProps = getAvatarContentProps(passedProps);

  // Compute the badge size once here so the reserved trailing space and the
  // badge itself derive from a single value; `undefined` means no badge.
  const {
    socialNetwork
  } = avatarContentWrapperProps;
  const avatarPx = AVATAR_SIZES[size];
  const socialBadgeSize = shouldShowSocialBadge(socialNetwork, size) && typeof avatarPx === 'number' ? getSocialBadgeSize(socialNetwork, avatarPx) : undefined;
  const containerDataTestIds = {
    'data-test-id': passedProps['data-test-id'] || 'AvatarDisplay-avatarContent',
    'data-avatar-loaded': isHubspotUserAvatar ? isHubspotUserAvatarLoaded : isLoaded
  };
  const avatarContainer = /*#__PURE__*/_jsx(AvatarContainer, Object.assign({}, avatarHoverProps, {
    children: /*#__PURE__*/_jsx(AvatarContentWrapper, Object.assign({}, avatarContentWrapperProps, {
      badgeSize: socialBadgeSize,
      children: /*#__PURE__*/_jsx(BaseContainer, Object.assign({}, containerDataTestIds, {
        children: /*#__PURE__*/_jsx(AvatarContent, Object.assign({}, avatarContentProps))
      }))
    }))
  }));

  // Only wrap (and reserve trailing space) when a badge actually renders.
  // Otherwise render the avatar exactly as before, so responsive avatars and
  // logo-less networks keep their original layout and size.
  const avatarContent = socialBadgeSize !== undefined ? /*#__PURE__*/_jsx(SocialAvatarWrapper, {
    "data-test-id": "AvatarDisplay-social-wrapper",
    $reserve: socialBadgeSize + SOCIAL_BADGE_GAP,
    children: avatarContainer
  }) : avatarContainer;
  if (children) {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [avatarContent, /*#__PURE__*/_jsx(Caption, {
        childMarginInlineStart: passedProps.childMarginInlineStart,
        childTruncateLength: passedProps.childTruncateLength,
        onClick: onClick,
        children: children
      })]
    });
  }
  return avatarContent;
};
FoundationsAvatarDisplay.displayName = 'FoundationsAvatarDisplay';
export const AvatarDisplay = props => {
  const {
    isTrellisEnabled
  } = useTrellisMigration();
  const {
    size,
    onClick,
    href,
    children,
    socialNetwork,
    displayName,
    showTooltip,
    tooltipPlacement,
    tooltipTitle,
    isEditable
  } = props;
  const hasBorderOverride = Object.values(getBorderProps(props)).some(value => value !== undefined);
  const hasMarginOverride = Object.values(getMarginProps(props)).some(value => value !== undefined);

  // TRELLIS-TODO: https://github.com/HubSpotEngineering/trellis-ux-support/issues/382
  // Trellis Avatar can't yet match these Foundations paths, so keep them
  // transitional until Trellis adds support -- sizes outside xs/sm/md
  // (xxs/lg/xl/responsive), onClick, href, isEditable, socialNetwork, children
  // caption, tooltips, and border/margin style props.
  const showsDefaultTooltip = showTooltip !== false && Boolean(displayName);
  const trellisCanRender = (size === undefined || size === 'xs' || size === 'sm' || size === 'md') && !onClick && !href && !children && !socialNetwork && !showsDefaultTooltip && !tooltipPlacement && !tooltipTitle && !isEditable && !hasBorderOverride && !hasMarginOverride;
  return isTrellisEnabled && trellisCanRender ? /*#__PURE__*/_jsx(TrellisAvatarDisplay, Object.assign({}, props)) : /*#__PURE__*/_jsx(FoundationsAvatarDisplay, Object.assign({}, props));
};
AvatarDisplay.displayName = 'AvatarDisplay';